import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { env } from '../lib/runtime-env';
import { handleSseEvent, type SseEvent } from '../lib/sse-event-emitter';
import { useAuthStore } from '../stores/useAuthStore';

interface UseSseConnectionResult {
  connected: boolean;
}

/**
 * Story 8.5 — Hook que mantém EventSource aberto contra `/api/sse/identities`.
 *
 * EventSource não suporta headers customizados (sem Authorization). Solução
 * MVP: passa accessToken via query string `?token=...`. Backend (8.5) faz
 * fallback de leitura do token via query string quando header ausente.
 *
 * Reconnect: nativo do EventSource (retry com backoff exponencial).
 * Fallback: se onerror muitas vezes seguidas, dispara fallback polling 30s
 * em TanStack Query (já configurado nos hooks principais).
 */
export function useSseConnection(path: string = '/identities'): UseSseConnectionResult {
  const qc = useQueryClient();
  const token = useAuthStore((state) => state.accessToken);
  const [connected, setConnected] = useState(false);
  const sourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!token) {
      setConnected(false);
      return;
    }

    const url = `${env.apiUrl}/api/sse${path}?token=${encodeURIComponent(token)}`;
    const source = new EventSource(url, { withCredentials: true });
    sourceRef.current = source;

    source.onopen = () => setConnected(true);
    source.onerror = () => setConnected(false);
    source.onmessage = (event: MessageEvent<string>) => {
      try {
        const parsed = JSON.parse(event.data) as SseEvent;
        if (parsed.type === 'heartbeat') return;
        handleSseEvent(parsed, qc);
      } catch {
        // ignora parse errors
      }
    };

    return () => {
      source.close();
      sourceRef.current = null;
      setConnected(false);
    };
  }, [token, path, qc]);

  return { connected };
}
